import { z } from "zod";

/**
 * Shared password rule for sign-up and reset.
 *
 * Supabase Auth minimum length must be set to 8 in the Supabase dashboard
 * (Authentication → Providers → Email → password minimum). This module does
 * not change that project setting and does not ship a migration.
 *
 * NIST SP 800-63B: length plus a blocklist. No composition rules.
 */
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 72;

const COMMON_PASSWORDS = new Set(
  "password,password1,password123,123456,12345678,123456789,1234567890,qwerty,qwerty123,abc123,letmein,iloveyou,admin,welcome,monkey,dragon,master,login,passw0rd,football,soccer,baseball,princess,shadow,sunshine,trustno1,123123,111111,000000,654321,superman,batman,michael,jordan,liverpool,chelsea,arsenal,manchester,malaysia,fanjago,changeme,secret,default,guest,root,pass,pass123,password!,qwertyuiop,asdfgh,zxcvbnm,1q2w3e4r,1qaz2wsx,aa123456,abc12345,password12,welcome1,admin123,letmein1,iloveyou1,starwars,whatever,trustme,access,flower,hello123,freedom,whatever1,qazwsx,michael1,football1,soccer1,baseball1"
    .split(","),
);

export type PasswordIdentity = {
  email?: string;
  displayName?: string;
};

export type PasswordIssue = "min" | "max" | "identity" | "common";

export type PasswordStrength = 0 | 1 | 2 | 3 | 4;

export function isCommonPassword(password: string): boolean {
  return COMMON_PASSWORDS.has(password.trim().toLowerCase());
}

export function containsIdentity(password: string, identity?: PasswordIdentity): boolean {
  const value = password.trim().toLowerCase();
  if (!value) return false;
  const email = identity?.email?.trim().toLowerCase() ?? "";
  const local = email.split("@")[0] ?? "";
  if (local && (value === local || (local.length >= 3 && value.includes(local)))) return true;
  const name = identity?.displayName?.trim().toLowerCase() ?? "";
  if (name && value === name) return true;
  return name.split(/\s+/).some((part) => part.length >= 3 && value.includes(part));
}

export function passwordIssues(password: string, identity?: PasswordIdentity): PasswordIssue[] {
  const issues: PasswordIssue[] = [];
  if (password.length < PASSWORD_MIN) issues.push("min");
  if (password.length > PASSWORD_MAX) issues.push("max");
  if (containsIdentity(password, identity)) issues.push("identity");
  if (isCommonPassword(password)) issues.push("common");
  return issues;
}

export function passwordChecklist(password: string, identity?: PasswordIdentity) {
  const started = password.length > 0;
  return {
    length: password.length >= PASSWORD_MIN && password.length <= PASSWORD_MAX,
    identity: started && !containsIdentity(password, identity),
    common: started && !isCommonPassword(password),
  };
}

/** Local 0–4 score. Not zxcvbn. Colour is never the only signal. */
export function passwordStrength(password: string): PasswordStrength {
  if (!password) return 0;
  if (isCommonPassword(password) || password.length < PASSWORD_MIN) return 1;
  let score = 1;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(password)).length;
  if (kinds >= 3 && password.length >= 10) score += 1;
  if (/(.)\1{2,}/.test(password)) score -= 1;
  return Math.max(1, Math.min(4, score)) as PasswordStrength;
}

export function passwordFieldSchema(identity?: PasswordIdentity) {
  return z.string().superRefine((value, ctx) => {
    for (const issue of passwordIssues(value, identity)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue });
    }
  });
}

export function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  return `${local.slice(0, 1)}•••@${domain}`;
}
